import { useEffect, useMemo } from "react";
import { motion } from "motion/react";

import { AnalysisReportEvents } from "shared/types";
import { type Conversation } from "shared/apiClient";
import { AccountType, AssessmentLights } from "shared/types";
import { cn, getText, interpolate, type Noticeboard } from "shared/lib";
import { animationVariants } from "./lib";

import {
  useAssessmentLights,
  useIsVicSmartEnabled,
  useIsVicSmartStandardApplicationRequirementsEnabled,
} from "shared/hooks";
import {
  useGetReportLoadingIndicatorConfig,
  useLatestMessage,
  useSkipAnalysisLayoutAnimations,
} from "@/hooks";
import {
  useIsReportGenerating,
  useIsReportReGenerating,
} from "@/components/analysisLayout/hooks";

import { ApplicationRequirementsReport, Separator } from "shared/components";
import { AnalysisTimeline } from "@/components/analysisTimeline";
import { ReportWarningPanel } from "@/components/wizard/ReportWarningPanel";
import { ApplicationRequirementsLoaderContainer } from "@/components/analysisLayout/applicationRequirementsLoader";
import { ExemptionsMayApplyCallout } from "./ExemptionsMayApplyCallout";
import { MultiLingualDisclaimer } from "@/components/analysisLayout/multiLingualDisclaimer";

export interface ApplicationRequirementsProps {
  conversation: Conversation | undefined | null;
  messages: Conversation["messages"];
  noticeboardData: Noticeboard | undefined | null;
  printable?: boolean;
}

export function ApplicationRequirements({
  conversation,
  messages,
  noticeboardData,
  printable,
}: ApplicationRequirementsProps) {
  const [skipAnimations, setSkipAnimations] = useSkipAnalysisLayoutAnimations();

  useEffect(() => {
    setTimeout(() => {
      setSkipAnimations(true);
    }, 1000);
  }, [setSkipAnimations]);

  const latestUserMessage = useLatestMessage({
    senderAccountType: AccountType.USER,
  });

  const assessmentLights = useAssessmentLights(
    conversation,
    noticeboardData,
    "enquiry",
  );

  const reportGenerating = useIsReportGenerating(conversation);
  const reportReGenerating = useIsReportReGenerating(conversation);

  const mergedConversation = useMemo(() => {
    return {
      ...conversation,
      messages: [...(messages || [])],
    };
  }, [conversation, messages]);

  const isVicSmartEnabled = useIsVicSmartEnabled(conversation, "enquiry");
  const isVicSmartStandardApplicationRequirementsEnabled =
    useIsVicSmartStandardApplicationRequirementsEnabled(
      conversation,
      "enquiry",
    );

  const reportLoadingIndicatorConfig = useGetReportLoadingIndicatorConfig(
    conversation,
    noticeboardData,
  );

  return (
    <div className={cn("flex flex-col gap-10", printable && "gap-5")}>
      <div className="overflow-hidden">
        <motion.div
          className="flex flex-col gap-1 [transform-origin:top_left]"
          variants={animationVariants}
          initial={!skipAnimations && "hidden"}
          animate={["heading", "opacity"]}
        >
          <h2 className="text-textPrimary text-heading2xl">
            {getText("Application requirements")}
          </h2>

          <p className="text-textSubdued text-bodylg">
            {getText(
              "If your proposal requires a planning permit, what documents and other information you'll need to provide when you apply.",
            )}
          </p>
        </motion.div>
      </div>

      <div className="empty:hidden" id="content-header-portal" />

      {reportGenerating ? (
        <div className="overflow-hidden">
          <motion.div
            className="[transform-origin:top_left]"
            variants={animationVariants}
            initial={!skipAnimations && "hidden"}
            animate={["loader", "opacity"]}
          >
            <ApplicationRequirementsLoaderContainer
              noticeboardData={noticeboardData}
              latestUserMessage={latestUserMessage}
              progressIndicatorItemMap={reportLoadingIndicatorConfig}
              title={getText("🤓 Generating application requirements")}
            />
          </motion.div>
        </div>
      ) : null}

      {reportReGenerating ? (
        <div className="overflow-hidden">
          <motion.div
            className="[transform-origin:top_left]"
            variants={animationVariants}
            initial={!skipAnimations && "hidden"}
            animate={["loader", "opacity"]}
          >
            <ApplicationRequirementsLoaderContainer
              noticeboardData={noticeboardData}
              latestUserMessage={latestUserMessage}
              progressIndicatorItemMap={reportLoadingIndicatorConfig}
              title={getText("🤓 Re-generating application requirements")}
            />
          </motion.div>
        </div>
      ) : null}

      {!reportGenerating && !reportReGenerating ? (
        <>
          <div className="overflow-hidden">
            <motion.div
              className="[transform-origin:top_left]"
              variants={animationVariants}
              initial={!skipAnimations && "hidden"}
              animate={["timeline", "opacity"]}
            >
              <AnalysisTimeline conversation={mergedConversation} />
            </motion.div>
          </div>

          <div className="overflow-hidden">
            <motion.div
              className="flex flex-col gap-10 [transform-origin:top_left]"
              variants={animationVariants}
              initial={!skipAnimations && "hidden"}
              animate={["report", "opacity"]}
            >
              <div className="flex flex-col gap-5">
                {assessmentLights ===
                AssessmentLights.MoreInformationRequired ? (
                  <ExemptionsMayApplyCallout
                    onClickPermitApplication={() => {
                      const showPermitRequirementsCustomEvent = new CustomEvent(
                        AnalysisReportEvents.SHOW_PERMIT_REQUIREMENTS,
                      );
                      document.dispatchEvent(showPermitRequirementsCustomEvent);
                    }}
                  />
                ) : null}

                <ApplicationRequirementsReport
                  conversation={mergedConversation}
                  messages={messages || []}
                  noticeboardData={noticeboardData}
                  printable={printable}
                  translate={{
                    getText,
                    interpolate,
                  }}
                  configPrefix="enquiry"
                  showVicSmart={isVicSmartEnabled}
                  isVicSmartStandardApplicationRequirementsEnabled={
                    isVicSmartStandardApplicationRequirementsEnabled
                  }
                />
              </div>

              {printable ? null : (
                <>
                  <div className="empty:hidden" id="content-footer-portal" />

                  <Separator orientation="horizontal" />

                  <ReportWarningPanel conversation={mergedConversation} />

                  <MultiLingualDisclaimer />
                </>
              )}
            </motion.div>
          </div>
        </>
      ) : null}
    </div>
  );
}
